// Price breakdown — mirrors the WhatsApp bot so the dashboard quotes match what
// guests see. Rates are NOT hardcoded: they are configured at runtime from the
// hotel's payments config (Settings → tax_rate / service_charge / deposit).
// Defaults are INCLUSIVE (tax 0 / service 0) so the dashboard never shows phantom
// tax before the config loads. `total` is always the all-in price.

// Mutable runtime rates — set via configurePricing() from the hotel table.
let TAX_RATE       = 0;     // payments.tax            (0 = rates already include VAT)
let SERVICE_RATE   = 0;     // payments.service_charge (0 = rates already include service)
let DEPOSIT_RATE   = 0.05;  // payments.deposit.percentage — fallback only (day-use / nights unknown)
let DEPOSIT_NIGHTS = 1;     // deposit = this many of the most-expensive nights of the stay

export function configurePricing(cfg: {
  tax?: number; service?: number; depositPercentage?: number; depositNights?: number;
}) {
  if (cfg.tax != null && !isNaN(cfg.tax)) TAX_RATE = cfg.tax;
  if (cfg.service != null && !isNaN(cfg.service)) SERVICE_RATE = cfg.service;
  if (cfg.depositPercentage != null && !isNaN(cfg.depositPercentage)) DEPOSIT_RATE = cfg.depositPercentage;
  if (cfg.depositNights != null && !isNaN(cfg.depositNights) && cfg.depositNights > 0) DEPOSIT_NIGHTS = cfg.depositNights;
}

export function getPricingRates() {
  return { tax: TAX_RATE, service: SERVICE_RATE, depositPercentage: DEPOSIT_RATE, depositNights: DEPOSIT_NIGHTS };
}

export interface Breakdown {
  subtotal: number;   // sum of nightly room rates (pre-tax)
  tax: number;        // tax on subtotal
  service: number;    // service charge on subtotal
  total: number;      // grand total (subtotal + tax + service)
  deposit: number;    // deposit to collect now (N nights of the stay)
  taxRate: number;    // the rate used (for the label, and to hide the row when 0)
  serviceRate: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

// Deposit = N most-expensive nights of the stay (default 1 night). With a uniform
// per-night rate, total/nights IS the most expensive night. Falls back to the %
// rule only when nights is unknown (e.g. day use).
function depositFor(total: number, nights?: number): number {
  if (nights && nights > 0) return round2((total / nights) * Math.min(DEPOSIT_NIGHTS, nights));
  return round2(total * DEPOSIT_RATE);
}

// Build from the pre-tax subtotal (e.g. walk-in: rate × nights + extras).
export function breakdownFromSubtotal(subtotal: number, nights?: number): Breakdown {
  const tax     = subtotal * TAX_RATE;
  const service = subtotal * SERVICE_RATE;
  const total   = subtotal + tax + service;
  return {
    subtotal: round2(subtotal), tax: round2(tax), service: round2(service),
    total: round2(total), deposit: depositFor(total, nights),
    taxRate: TAX_RATE, serviceRate: SERVICE_RATE,
  };
}

// Build from a stored grand total (post-tax) — reverse out the subtotal.
export function breakdownFromTotal(total: number, nights?: number): Breakdown {
  const subtotal = total / (1 + TAX_RATE + SERVICE_RATE);
  return breakdownFromSubtotal(subtotal, nights);
}

export const money = (n: number, cur = "EGP") =>
  `${cur} ${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
